import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { ToastProvider } from "@/context/ToastContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { AuthProvider } from "@/context/AuthContext";
import { PWAProvider } from "@/context/PWAContext";

import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";

import { LoginPage } from "@/pages/auth/LoginPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { SetPasswordPage } from "@/pages/auth/SetPasswordPage";
import { ChangePasswordPage } from "@/pages/auth/ChangePasswordPage";

import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import TasksPage from "@/pages/tasks/TasksPage";
import CalendarPage from "@/pages/calendar/CalendarPage";
import { LeadsPage } from "@/pages/leads/LeadsPage";
import { LeadFormPage } from "@/pages/leads/LeadFormPage";
import { LeadDetailPage } from "@/pages/leads/LeadDetailPage";
import { DealsPage } from "@/pages/deals/DealsPage";
import { DealDetailPage } from "@/pages/deals/DealDetailPage";
import { AccountsPage } from "@/pages/accounts/AccountsPage";
import { AccountDetailPage } from "@/pages/accounts/AccountDetailPage";
import { ContactsPage } from "@/pages/contacts/ContactsPage";
import { ContactDetailPage } from "@/pages/contacts/ContactDetailPage";
import { ProductsPage } from "@/pages/products/ProductsPage";
import { AgentsPage } from "@/pages/agents/AgentsPage";
import { SamplesPage } from "@/pages/samples/SamplesPage";
import { OrdersPage } from "@/pages/orders/OrdersPage";
import { OrderDetailPage } from "@/pages/orders/OrderDetailPage";
import { ComplaintsPage } from "@/pages/complaints/ComplaintsPage";
import { InventoryPage } from "@/pages/inventory/InventoryPage";
import { GoodsReceiptsPage } from "@/pages/inventory/GoodsReceiptsPage";
import { WarehousePage } from "@/pages/inventory/WarehousePage";
import { SuppliersPage } from "@/pages/suppliers/SuppliersPage";
import { ProductionPage } from "@/pages/production/ProductionPage";
import { LabelsPage } from "@/pages/inventory/LabelsPage";
import { QualityPage } from "@/pages/inventory/QualityPage";
import { InventoryReportsPage } from "@/pages/inventory/InventoryReportsPage";
import { ProductionReportPage } from "@/pages/production/ProductionReportPage";
import { WorkOrderDetailPage } from "@/pages/production/WorkOrderDetailPage";
import { ProcessesPage } from "@/pages/settings/ProcessesPage";
import { QuotesPage } from "@/pages/quotes/QuotesPage";
import { QuoteEditorPage } from "@/pages/quotes/QuoteEditorPage";
import { UsersPage } from "@/pages/users/UsersPage";
import { RolesPage } from "@/pages/roles/RolesPage";
import { CompanyPage } from "@/pages/settings/CompanyPage";
import { CurrencyPage } from "@/pages/settings/CurrencyPage";
import { CountryPage } from "@/pages/settings/CountryPage";
import { PipelinePage } from "@/pages/settings/PipelinePage";
import { SettingRulePage } from "@/pages/settings/SettingRulePage";
import { AuditLogPage } from "@/pages/audit/AuditLogPage";
import { MasterLookupPage } from "@/pages/settings/MasterLookupPage";
import { ReportsPage } from "@/pages/reports/ReportsPage";
import { UserReportPage } from "@/pages/reports/UserReportPage";
import { TargetsPage } from "@/pages/targets/TargetsPage";
import { EmailsPage } from "@/pages/emails/EmailsPage";
import { WhatsAppPage } from "@/pages/whatsapp/WhatsAppPage";
import { WhatsAppSettingsPage } from "@/pages/settings/WhatsAppSettingsPage";
import { EmailSettingsPage } from "@/pages/settings/EmailSettingsPage";
import { LeadSourcesPage } from "@/pages/settings/LeadSourcesPage";

import "./index.css";

export function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <PWAProvider>
              <Routes>
              {/* Public Authentication Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              {/* Old reset-code page: codes are retired, so send people to request a link */}
              <Route path="/reset-password/*" element={<Navigate to="/forgot-password" replace />} />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected Management Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route
                  path="/emails"
                  element={
                    <ProtectedRoute permission="email.read">
                      <EmailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/mailbox" element={<Navigate to="/emails?tab=settings" replace />} />
                <Route
                  path="/whatsapp"
                  element={
                    <ProtectedRoute permission="whatsapp.read">
                      <WhatsAppPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute permission="calendar.read">
                      <CalendarPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute permission="activity.read">
                      <TasksPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute permission="lead.read">
                      <LeadsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads/new"
                  element={
                    <ProtectedRoute permission="lead.create">
                      <LeadFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads/:id/edit"
                  element={
                    <ProtectedRoute permission="lead.update">
                      <LeadFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads/:id"
                  element={
                    <ProtectedRoute permission="lead.read">
                      <LeadDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deals"
                  element={
                    <ProtectedRoute permission="deal.read">
                      <DealsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deals/:id"
                  element={
                    <ProtectedRoute permission="deal.read">
                      <DealDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <ProtectedRoute permission="product.read">
                      <ProductsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <ProtectedRoute permission="order.read">
                      <OrdersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/:id"
                  element={
                    <ProtectedRoute permission="order.read">
                      <OrderDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute permission="inventory.read">
                      <InventoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/receipts"
                  element={
                    <ProtectedRoute permission="inventory.read">
                      <GoodsReceiptsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/warehouses"
                  element={
                    <ProtectedRoute permission="inventory.read">
                      <WarehousePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/production"
                  element={
                    <ProtectedRoute permission="production.read">
                      <ProductionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/labels"
                  element={
                    <ProtectedRoute permission="inventory.read">
                      <LabelsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/quality"
                  element={
                    <ProtectedRoute permission="inventory.read">
                      <QualityPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/reports"
                  element={
                    <ProtectedRoute permission="inventory.read">
                      <InventoryReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/production/report"
                  element={
                    <ProtectedRoute permission="production.read">
                      <ProductionReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/production/:id"
                  element={
                    <ProtectedRoute permission="production.read">
                      <WorkOrderDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/processes"
                  element={
                    <ProtectedRoute permission="production.read">
                      <ProcessesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/suppliers"
                  element={
                    <ProtectedRoute permission="inventory.read">
                      <SuppliersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/complaints"
                  element={
                    <ProtectedRoute permission="complaint.read">
                      <ComplaintsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/samples"
                  element={
                    <ProtectedRoute permission="sample.read">
                      <SamplesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents"
                  element={
                    <ProtectedRoute permission="agent.read">
                      <AgentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quotes"
                  element={
                    <ProtectedRoute permission="quote.read">
                      <QuotesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quotes/:id"
                  element={
                    <ProtectedRoute permission="quote.read">
                      <QuoteEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute permission="report.view_own">
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/user/:id"
                  element={
                    <ProtectedRoute permission="report.view_own">
                      <UserReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/targets"
                  element={
                    <ProtectedRoute permission="target.read">
                      <TargetsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts"
                  element={
                    <ProtectedRoute permission="account.read">
                      <AccountsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts/:id"
                  element={
                    <ProtectedRoute permission="account.read">
                      <AccountDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts"
                  element={
                    <ProtectedRoute permission="contact.read">
                      <ContactsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts/:id"
                  element={
                    <ProtectedRoute permission="contact.read">
                      <ContactDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute permission="user.manage">
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/roles"
                  element={
                    <ProtectedRoute permission="role.manage">
                      <RolesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/company"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <CompanyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/currencies"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <CurrencyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/countries"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <CountryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/pipelines"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <PipelinePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/rules"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <SettingRulePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/audit-log"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <AuditLogPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/settings/security-log" element={<Navigate to="/audit-log" replace />} />
                <Route
                  path="/settings/whatsapp"
                  element={
                    <ProtectedRoute permission="whatsapp.admin">
                      <WhatsAppSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/lead-sources"
                  element={
                    <ProtectedRoute permission="lead.source_manage">
                      <LeadSourcesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/email"
                  element={<Navigate to="/emails?tab=settings" replace />}
                />
                <Route
                  path="/settings/mailbox"
                  element={<Navigate to="/emails?tab=settings" replace />}
                />
                <Route
                  path="/settings/masters"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <MasterLookupPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </PWAProvider>
        </AuthProvider>
        </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
